import posthog from 'posthog-js';
import { FaArrowRight } from 'react-icons/fa';
import { useEffect, useRef, useState, type JSX } from 'react';
import { checkPhone, signIn, signUp } from '@/services/slaydAPI';
import styles from './SignUpPage.module.css';

const genders = ['Male', 'Female'];

interface SignUpPageProps {
  autoFocus?: boolean;
  onAuthSuccess: (isSignUp: boolean, name: string) => void;
}

function SignUpPage({
  autoFocus = true,
  onAuthSuccess,
}: SignUpPageProps): JSX.Element {
  const [step, setStep] = useState(1);
  const [phone, setPhone] = useState('');
  const [error, setError] = useState('Enter a 10 digit number');
  const [loading, setLoading] = useState(false);
  const [name, setName] = useState('');
  const [gender, setGender] = useState('');
  const [instaHandle, setInstaHandle] = useState('');
  const [errorField, setErrorField] = useState<
    'phone' | 'name' | 'gender' | null
  >(null);
  const [keyboardHeight, setKeyboardHeight] = useState(0);

  const phoneInputRef = useRef<HTMLInputElement>(null);
  const nameInputRef = useRef<HTMLInputElement>(null);
  const instaInputRef = useRef<HTMLInputElement>(null);
  const selectRef = useRef<HTMLSelectElement>(null);

  const isPhoneValid = phone.length === 10;

  useEffect(() => {
    const handleResize = (): void => {
      const visualViewport = window.visualViewport;
      if (visualViewport) {
        // Calculate keyboard height based on window height minus visual viewport height
        const newKeyboardHeight = window.innerHeight - visualViewport.height;
        setKeyboardHeight(newKeyboardHeight > 50 ? newKeyboardHeight : 0);
      }
    };

    window.visualViewport?.addEventListener('resize', handleResize);
    return () =>
      window.visualViewport?.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    posthog.capture('signup_modal_opened', {
      source_page: window.location.pathname,
    });
  }, []);
  useEffect(() => {
    if (errorField === 'phone') phoneInputRef.current?.focus();
    else if (errorField === 'name') nameInputRef.current?.focus();
  }, [errorField]);

  useEffect(() => {
    if (!autoFocus) return;
    const focusInput = (): void => {
      if (step === 1) setTimeout(() => phoneInputRef.current?.focus(), 300);
      else if (step === 2) setTimeout(() => nameInputRef.current?.focus(), 100);
    };
    focusInput();
  }, [step, autoFocus]);

  const handlePhoneSubmit = async (): Promise<void> => {
    setError('');
    if (!isPhoneValid) {
      setError('Enter a 10 digit number');
      setErrorField('phone');
      return;
    }

    setLoading(true);
    try {
      const phoneStatus = await checkPhone(phone);
      setLoading(false);

      if (phoneStatus.exists) {
        setLoading(true);
        const loginResult = await signIn(phone);
        setLoading(false);

        if (loginResult.success) {
          posthog.capture('user_signed_in', {
            method: 'phone',
            phone_number: phone,
          });
          onAuthSuccess(false, loginResult.name);
        } else setError('Login failed');
      } else {
        setStep(2);
        posthog.capture('signup_step_1_completed', {
          step: 1,
          label: 'Entered phone number',
          phone_number: phone,
        });
      }
    } catch (err) {
      console.error(err);
      setError('Something went wrong');
      setLoading(false);
    }
  };

  const handleFinalSubmit = async (): Promise<void> => {
    setLoading(true);
    try {
      const registerResult = await signUp({ phone, name, gender, instaHandle });
      setLoading(false);

      if (registerResult.success) {
        posthog.capture('signup_step_2_completed', {
          step: 2,
          label: 'Entered name & gender',
          name,
          gender,
        });
        onAuthSuccess(true, registerResult.name);
      } else setError('Signup failed');
    } catch (err) {
      console.error(err);
      setError('Something went wrong');
      setLoading(false);
    }
  };

  // Derived class helpers
  const buttonEnabled = step === 1 ? isPhoneValid : name && gender;
  const buttonColorClass = buttonEnabled
    ? styles.buttonEnabled
    : styles.buttonDisabled;
  const buttonCursorClass = buttonEnabled ? styles.pointer : styles.notAllowed;

  return (
    <div className={styles.container}>
      <div className={styles.content}>
        <h2 className={styles.header}>
          {step === 1 ? 'Login/Sign-up' : "What's your name?"}
        </h2>

        {step === 1 ? (
          <>
            <div
              className={`${styles.inputContainer} ${
                error && errorField === 'phone' ? styles.errorOutline : ''
              }`}
            >
              <div className={styles.prefix}>+91</div>
              <input
                ref={phoneInputRef}
                type="tel"
                inputMode="numeric"
                maxLength={10}
                value={phone}
                placeholder="Enter mobile number"
                className={styles.input}
                onChange={(e) => setPhone(e.target.value.replace(/\D/g, ''))}
                onFocus={() => setError('')}
              />
            </div>

            {error && <p className={styles.error}>{error}</p>}
          </>
        ) : (
          <div className={styles.moreDetails}>
            <div
              className={`${errorField === 'name' ? styles.errorOutline : ''} ${styles.inputWrapper}`}
            >
              <input
                ref={nameInputRef}
                type="text"
                placeholder="Enter your name"
                value={name}
                className={styles.input}
                onChange={(e) => setName(e.target.value)}
              />
            </div>

            <div
              className={`${styles.selectWrapper} ${
                errorField === 'gender' ? styles.errorOutline : ''
              }`}
            >
              <select
                ref={selectRef}
                value={gender}
                className={`${styles.input} ${
                  gender === '' ? styles.placeholderSelect : ''
                } ${styles.customSelect}`}
                onChange={(e) => setGender(e.target.value)}
              >
                <option disabled hidden value="">
                  Gender
                </option>
                {genders.map((g) => (
                  <option key={g} value={g}>
                    {g}
                  </option>
                ))}
              </select>
            </div>

            <input
              ref={instaInputRef}
              type="text"
              placeholder="Instagram handle (optional)"
              value={instaHandle}
              className={styles.input}
              onChange={(e) => setInstaHandle(e.target.value)}
            />

            <p className={styles.instaNote}>
              *Improves personalisation based on what you see on Instagram
            </p>
          </div>
        )}
      </div>

      <div
        className={styles.buttonContainer}
        style={{ bottom: `${keyboardHeight + 20}px` }}
      >
        <button
          className={`${styles.button} ${buttonColorClass} ${buttonCursorClass}`}
          onClick={() => {
            if (step === 1) {
              if (!isPhoneValid) {
                setError('Enter a 10 digit number');
                setErrorField('phone');
                return;
              }
              setError('');
              setErrorField(null);
              handlePhoneSubmit();
            } else {
              if (!name) {
                setError('Please enter your name');
                setErrorField('name');
                return;
              }
              if (!gender) {
                setError('Please select your gender');
                setErrorField('gender');
                return;
              }
              setError('');
              setErrorField(null);
              handleFinalSubmit();
            }
          }}
        >
          {loading ? (
            <span className={styles.spinner} />
          ) : (
            <div className={styles.innerButton}>
              <span>Continue</span> <FaArrowRight />
            </div>
          )}
        </button>
      </div>
    </div>
  );
}

export default SignUpPage;
